import { useState, useEffect, useMemo } from 'react'
import type { PlanModulo, ElementoModulo, EstadoAlistamientoModulo } from './tipos'
import { ModalMoverAEtapa } from './ModalMoverAEtapa'
import { SelectorColaboradoresModulo } from './SelectorColaboradoresModulo'
import { ModalInsumosModulo } from './ModalInsumosModulo'
import {
  IconoGrip,
  IconoFlechaArriba,
  IconoFlechaAbajo,
  IconoBasura,
  IconoMoverEtapa,
  IconoColaborador,
  IconoCaja,
  IconoCalendario,
  IconoCheck,
  IconoReloj,
  IconoHerramienta,
  IconoCerrar,
  IconoAlerta,
} from '@/shared/ui/Iconos/Iconos'
import { CampoPesos } from './CampoPesos'
import { useBancoApus } from './apus/useBancoApus'
import { ModalEditarAiuApu } from './apus/ModalEditarAiuApu'
import { ModalReportarAvance } from './cortes/ModalReportarAvance'
import { calcularSemaforoAtraso } from './cortes/tipos'
import { useActasCorte } from './cortes/useActasCorte'
import { calcularFechasModulo, formatearFechaCorta, type InfoFechasModulo } from './utilidadesFechas'
import { ModalConfirmacion } from '@/shared/ui/Modal/ModalConfirmacion'
import estilos from './PlanProyectos.module.css'

function formatearMoneda(monto: number) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(monto)
}

function iniciales(nombre: string) {
  return nombre
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('')
}

function sanitizarNombreElemento(texto: string): string {
  if (!texto) return 'Material requerido'
  return texto
    .replace(/^suministro\s+e\s+instalaci[oó]n\s+de\s+/i, '')
    .replace(/^suministro\s+y\s+montaje\s+de\s+/i, '')
    .replace(/^suministro\s+de\s+/i, '')
    .replace(/^instalaci[oó]n\s+de\s+/i, '')
    .replace(/^actividad\s+de\s+/i, '')
    .replace(/^retiro\s+y\s+reubicaci[oó]n\s+de\s+/i, '')
    .trim()
}

function generarElementosIniciales(modulo: PlanModulo): ElementoModulo[] {
  if (Array.isArray(modulo.elementos)) {
    return modulo.elementos.map((e) => ({
      ...e,
      tipo: e.tipo || 'material',
      descripcion: sanitizarNombreElemento(e.descripcion),
    }))
  }
  return []
}



type EtapaOpcion = {
  id: string
  orden: number
  nombre: string
  modulos?: { id: string }[]
}

type PropsTarjeta = {
  modulo: PlanModulo
  indice: number
  totalModulos: number
  modoEdicion: boolean
  etapaFechaInicio?: string
  etapaFechaFin?: string
  fechasCalculadasProp?: InfoFechasModulo
  onActualizar: (cambios: Partial<PlanModulo>) => void
  onEliminar: () => void
  onMover: (direccion: 'arriba' | 'abajo') => void
  onDragStart: (e: React.DragEvent, index: number) => void
  onDragOver: (e: React.DragEvent, index: number) => void
  onDragLeave: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent, index: number) => void
  onDragEnd: (e: React.DragEvent) => void
  esArrastrando: boolean
  esDestino: boolean
  todasLasEtapas?: EtapaOpcion[]
  etapaActualId?: string
  onMoverAEtapa?: (etapaDestinoId: string) => void
  onAsignarColaboradores?: (colaboradoresIds: string[]) => void
  onActualizarAvanceColaborador?: (colaboradorId: string, nuevoAvance: number) => void
  seleccionado?: boolean
  onAlternarSeleccion?: () => void
}

export function TarjetaModuloEditable({
  modulo,
  indice,
  totalModulos,
  modoEdicion,
  etapaFechaInicio,
  etapaFechaFin,
  fechasCalculadasProp,
  onActualizar,
  onEliminar,
  onMover,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  esArrastrando,
  esDestino,
  todasLasEtapas = [],
  etapaActualId = modulo.etapa_id,
  onMoverAEtapa,
  onAsignarColaboradores,
  seleccionado,
  onAlternarSeleccion,
}: PropsTarjeta) {
  const [nombre, setNombre] = useState(modulo.nombre)
  const [codigo, setCodigo] = useState(modulo.codigo)
  const [horas, setHoras] = useState(modulo.horas)
  const [valorTotal, setValorTotal] = useState(modulo.valor_total)

  const { apus, actualizarAiuApu } = useBancoApus()
  const apuVinculado = useMemo(() => {
    if (!modulo.apu_id) return null
    return apus.find((a) => a.id === modulo.apu_id) || null
  }, [apus, modulo.apu_id])
  const [modalAiuApuAbierto, setModalAiuApuAbierto] = useState(false)

  // Cálculo automático de fechas por horas repartidas a lo largo de toda la etapa
  const fechasCalculadas = useMemo(() => {
    if (fechasCalculadasProp) return fechasCalculadasProp
    return calcularFechasModulo(modulo, {
      fecha_inicio: etapaFechaInicio || '',
      fecha_fin: etapaFechaFin || '',
    })
  }, [modulo, etapaFechaInicio, etapaFechaFin, fechasCalculadasProp])

  // Estado local para fechas editables in-situ
  const [fechaInicio, setFechaInicio] = useState(modulo.fecha_inicio || fechasCalculadas.fechaInicio)
  const [fechaFin, setFechaFin] = useState(modulo.fecha_fin || fechasCalculadas.fechaFin)

  const [elementos, setElementos] = useState<ElementoModulo[]>(() => generarElementosIniciales(modulo))

  useEffect(() => {
    if (Array.isArray(modulo.elementos)) {
      setElementos(modulo.elementos)
    }
  }, [modulo.elementos])

  const [modalMoverAbierto, setModalMoverAbierto] = useState(false)
  const [modalInsumosAbierto, setModalInsumosAbierto] = useState(false)
  const [editandoColaboradores, setEditandoColaboradores] = useState(false)
  const [modalReportarAvanceAbierto, setModalReportarAvanceAbierto] = useState(false)
  const [modalEliminarAbierto, setModalEliminarAbierto] = useState(false)
  const [modalSinInsumosAbierto, setModalSinInsumosAbierto] = useState(false)
  const { registrarCorteAvance } = useActasCorte()

  const semaforo = useMemo(() => {
    if (modulo.avance >= 100 || modulo.estado === 'hecho') {
      return { estado: 'al_dia' as const, atrasoPct: 0, esperadoPct: 100 }
    }
    return calcularSemaforoAtraso(
      modulo.avance,
      fechasCalculadas.fechaInicio,
      fechasCalculadas.fechaFin
    )
  }, [modulo.avance, modulo.estado, fechasCalculadas.fechaInicio, fechasCalculadas.fechaFin])

  const handleGuardarCorteAvance = (datos: {
    cantidadCorte: number
    quienReporto: string
    fotoEvidenciaUrl?: string | null
    observaciones?: string
  }) => {
    const contratada = Math.max(0.01, Number(modulo.cantidad_contratada || modulo.cantidad || 0))
    const cantAnterior = Number(modulo.cantidad_ejecutada || 0)
    const nuevaEjecutada = Number((cantAnterior + datos.cantidadCorte).toFixed(2))
    const nuevoAvance = Math.min(100, Math.round((nuevaEjecutada / contratada) * 100))
    const nuevoEstado = nuevoAvance === 100 ? 'hecho' : 'en_ejecucion'

    onActualizar({
      cantidad_ejecutada: nuevaEjecutada,
      avance: nuevoAvance,
      estado: nuevoEstado,
    })

    void registrarCorteAvance({
      moduloId: modulo.id,
      proyectoId: etapaActualId || modulo.etapa_id,
      etapaId: modulo.etapa_id,
      cantidadContratada: contratada,
      cantidadAnterior: cantAnterior,
      cantidadCorte: datos.cantidadCorte,
      quienReporto: datos.quienReporto,
      fotoEvidenciaUrl: datos.fotoEvidenciaUrl,
      observaciones: datos.observaciones,
    })
  }

  function handleGuardarInsumos(nuevosElementos: ElementoModulo[]) {
    setElementos(nuevosElementos)
    onActualizar({ elementos: nuevosElementos })
    setModalInsumosAbierto(false)
  }

  function guardarCambiosTexto() {
    const horasNum = Math.round(Number(horas)) || 1
    onActualizar({
      nombre: nombre.trim() || modulo.nombre,
      codigo: codigo.trim() || modulo.codigo,
      horas: horasNum,
      horas_programadas: horasNum,
      valor_total: Number(valorTotal) || 0,
    })
  }

  function handleCambioFechaInicio(valor: string) {
    let fechaAjustada = valor
    if (etapaFechaInicio && valor && valor < etapaFechaInicio) fechaAjustada = etapaFechaInicio
    if (etapaFechaFin && valor && valor > etapaFechaFin) fechaAjustada = etapaFechaFin
    setFechaInicio(fechaAjustada)
    onActualizar({ fecha_inicio: fechaAjustada || null })
  }

  function handleCambioFechaFin(valor: string) {
    let fechaAjustada = valor
    if (fechaInicio && valor && valor < fechaInicio) fechaAjustada = fechaInicio
    if (etapaFechaFin && valor && valor > etapaFechaFin) fechaAjustada = etapaFechaFin
    setFechaFin(fechaAjustada)
    onActualizar({ fecha_fin: fechaAjustada || null })
  }

  function handleTransicionAlistamiento(nuevoEstado: EstadoAlistamientoModulo) {
    if (nuevoEstado === 'en_solicitud' && elementos.length === 0) {
      setModalSinInsumosAbierto(true)
      return
    }
    onActualizar({
      alistamiento_estado: nuevoEstado,
      alistamiento_fecha: new Date().toISOString(),
      elementos,
    })
  }

  const clasesTarjeta = [
    estilos.tarjetaModulo,
    modoEdicion ? estilos.tarjetaModoEdicion : '',
    modoEdicion ? estilos.tarjetaArrastrable : '',
    esArrastrando ? estilos.tarjetaArrastrando : '',
    esDestino ? estilos.tarjetaDestinoHover : '',
  ]
    .filter(Boolean)
    .join(' ')

  const esHecho = modulo.avance === 100 || modulo.estado === 'hecho'

  function handleToggleCompletado() {
    if (esHecho) {
      onActualizar({
        avance: 0,
        estado: 'pendiente',
        cantidad_ejecutada: 0,
        horas_ejecutadas: 0,
      })
    } else {
      const contratada = modulo.cantidad_contratada || modulo.cantidad || 1
      const horasEst = Number(modulo.horas_programadas) > 0 ? Number(modulo.horas_programadas) : (Number(modulo.horas) || 0)
      onActualizar({
        avance: 100,
        estado: 'hecho',
        cantidad_ejecutada: contratada,
        horas_ejecutadas: Number(modulo.horas_ejecutadas) > 0 ? Number(modulo.horas_ejecutadas) : horasEst,
      })
    }
  }

  const tieneOtrasEtapas = todasLasEtapas.some((e) => e.id !== etapaActualId)
  const tieneColaboradores = Boolean(modulo.colaboradores && modulo.colaboradores.length > 0)
  const estadoAlistamiento = modulo.alistamiento_estado || 'sin_alistar'

  const materiales = elementos.filter((e) => e.tipo === 'material' || !e.tipo)
  const herramientas = elementos.filter((e) => e.tipo === 'herramienta')

  return (
    <>
      <article
        className={clasesTarjeta}
        draggable={modoEdicion}
        onDragStart={(e) => {
          if (!modoEdicion) {
            e.preventDefault()
            return
          }
          onDragStart(e, indice)
        }}
        onDragOver={(e) => onDragOver(e, indice)}
        onDragLeave={onDragLeave}
        onDrop={(e) => onDrop(e, indice)}
        onDragEnd={onDragEnd}
      >
        {/* Barra superior de la tarjeta: Código y Controles */}
        <header className={estilos.moduloHeader}>
          <div className={estilos.moduloHeaderIzq}>
            <label className={estilos.checkboxContenedor} title="Seleccionar para mover en lote">
              <input
                type="checkbox"
                checked={Boolean(seleccionado)}
                onChange={onAlternarSeleccion}
                className={estilos.checkboxModulo}
              />
            </label>
            {modoEdicion && (
              <span className={estilos.iconoGripHandle} title="Arrastrar para cambiar orden">
                <IconoGrip />
              </span>
            )}

            {modoEdicion ? (
              <input
                type="text"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                onBlur={guardarCambiosTexto}
                className={estilos.inputCodigoInline}
                title="Código del ítem"
              />
            ) : (
              <span className={estilos.moduloCodigo}>{modulo.codigo}</span>
            )}

            {apuVinculado && (
              <button
                type="button"
                onClick={() => setModalAiuApuAbierto(true)}
                style={{
                  background: '#f3e8ff',
                  color: '#6b21a8',
                  border: '1px solid #d8b4fe',
                  padding: '1px 6px',
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  marginLeft: '4px',
                }}
                title={`APU vinculado: ${apuVinculado.codigo} (${apuVinculado.nombre}). Clic para modificar valores del AIU.`}
              >
                APU {apuVinculado.aiu.porcentaje_admin + apuVinculado.aiu.porcentaje_utilidad + apuVinculado.aiu.porcentaje_imprevistos}%
              </button>
            )}
          </div>

          <div className={estilos.moduloHeaderDer}>
            {/* Botón Rápido Completado / Reabrir (KISS) */}
            <button
              type="button"
              className={esHecho ? estilos.btnModuloHechoActivo : estilos.btnModuloMarcarHecho}
              onClick={handleToggleCompletado}
              title={
                esHecho
                  ? 'Módulo completado al 100%. Clic para desmarcar y reabrir.'
                  : 'Marcar como 100% completado (actualiza Curva S y P&G al instante)'
              }
            >
              <IconoCheck style={{ width: '12px', height: '12px' }} />
              <span>{esHecho ? 'Hecho' : 'Completar'}</span>
            </button>
            {tieneOtrasEtapas && !modoEdicion && (
              <button
                type="button"
                className={estilos.btnMoverEtapaModal}
                onClick={() => setModalMoverAbierto(true)}
                title="Mover a otra etapa"
              >
                <IconoMoverEtapa />
              </button>
            )}

            {modoEdicion && (
              <div className={estilos.controlesOrden}>
                <button
                  type="button"
                  className={estilos.btnFlechaOrden}
                  disabled={indice === 0}
                  onClick={() => onMover('arriba')}
                  title="Subir orden"
                >
                  <IconoFlechaArriba />
                </button>
                <button
                  type="button"
                  className={estilos.btnFlechaOrden}
                  disabled={indice === totalModulos - 1}
                  onClick={() => onMover('abajo')}
                  title="Bajar orden"
                >
                  <IconoFlechaAbajo />
                </button>
                <button
                  type="button"
                  className={estilos.btnEliminarModulo}
                  onClick={() => setModalEliminarAbierto(true)}
                  title="Eliminar módulo"
                >
                  <IconoBasura />
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Nombre / Tarea: Editable in situ en modo edición */}
        {modoEdicion ? (
          <textarea
            rows={2}
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            onBlur={guardarCambiosTexto}
            className={estilos.textareaNombreInline}
            title="Nombre de la tarea (editable)"
          />
        ) : (
          <p className={estilos.moduloNombre}>{modulo.nombre}</p>
        )}

        {/* Fechas del Módulo: Repartidas automáticamente en lectura, Editables en edición */}
        {modoEdicion ? (
          <div className={estilos.moduloFechasEdicionFila}>
            <label className={estilos.campoFechaEdicion} title="Fecha inicio dentro de la etapa">
              <span className={estilos.labelFechaEdicion}>Inicio:</span>
              <input
                type="date"
                value={fechaInicio}
                min={etapaFechaInicio}
                max={etapaFechaFin}
                onChange={(e) => handleCambioFechaInicio(e.target.value)}
                className={estilos.inputFechaModulo}
              />
            </label>
            <label className={estilos.campoFechaEdicion} title="Fecha fin dentro de la etapa">
              <span className={estilos.labelFechaEdicion}>Fin:</span>
              <input
                type="date"
                value={fechaFin}
                min={fechaInicio || etapaFechaInicio}
                max={etapaFechaFin}
                onChange={(e) => handleCambioFechaFin(e.target.value)}
                className={estilos.inputFechaModulo}
              />
            </label>
          </div>
        ) : (
          <div className={estilos.moduloFechaFila}>
            <span className={estilos.iconoFechaModulo} aria-hidden="true">
              <IconoCalendario />
            </span>
            <span className={estilos.fechaRangoTexto}>
              {formatearFechaCorta(fechasCalculadas.fechaInicio)} – {formatearFechaCorta(fechasCalculadas.fechaFin)}
            </span>
            <span className={estilos.badgeDuracion}>
              {fechasCalculadas.diasDuracion}d ({Math.round(modulo.horas || 8)}h)
            </span>
            {fechasCalculadas.esAutomatica && (
              <span
                className={estilos.chipAutoFecha}
                title="Repartido automáticamente a lo largo del calendario de la etapa según horas"
              >
                Auto
              </span>
            )}
          </div>
        )}

        {/* Valores y Horas */}
        <div className={estilos.moduloValores}>
          {modoEdicion ? (
            <div className={estilos.filaValoresEdicion}>
              <label>
                <span>COP</span>
                <CampoPesos
                  valor={valorTotal}
                  onCambio={(v) => {
                    setValorTotal(v)
                    onActualizar({ valor_total: v })
                  }}
                  className={estilos.inputValorInline}
                  title="Valor del módulo en COP"
                />
              </label>
              <label>
                <span>Horas</span>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={horas}
                  onChange={(e) => setHoras(Number(e.target.value))}
                  onBlur={guardarCambiosTexto}
                  className={estilos.inputHorasInline}
                />
              </label>
            </div>
          ) : (
            <>
              <span>{formatearMoneda(modulo.valor_total)}</span>
              <span>{Math.round(modulo.horas)} h est.</span>
            </>
          )}
        </div>

        {/* Comparativo de Horas Programadas vs Ejecutadas y Despacho */}
        {(() => {
          const progHoras = Number(modulo.horas_programadas) > 0 ? Number(modulo.horas_programadas) : (Number(modulo.horas) || 0)
          const ejecHoras = Number(modulo.horas_ejecutadas) > 0 ? Number(modulo.horas_ejecutadas) : (Number(modulo.horas_reales) || 0)
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 2px', fontSize: '11px', color: '#d1c4e9' }}>
              <span>Prog: <b style={{ color: '#ffffff' }}>{Math.round(progHoras)}h</b></span>
              <span style={{ color: ejecHoras > progHoras ? '#f87171' : '#34d399', fontWeight: 800 }}>
                Ejec: {Math.round(ejecHoras)}h
                {ejecHoras > progHoras && ' (desviación)'}
              </span>
              {Boolean(modulo.costo_materiales_despachados) && (
                <span title="Salidas reales de almacén despachadas" style={{ fontSize: '10.5px', color: '#ffc43f', fontWeight: 700 }}>
                  Salidas: {formatearMoneda(modulo.costo_materiales_despachados || 0)}
                </span>
              )}
            </div>
          )
        })()}

        {/* Alerta Preventiva de Stock en Bodega */}
        {estadoAlistamiento !== 'despachado' && materiales.length > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'rgba(245, 158, 11, 0.15)',
            border: '1px solid rgba(245, 158, 11, 0.4)',
            borderRadius: '6px',
            padding: '4px 8px',
            fontSize: '11px',
            color: '#fde68a',
            gap: '6px'
          }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
              <IconoAlerta style={{ width: '13px', height: '13px', color: '#fbbf24', flexShrink: 0 }} />
              <span><b>Falta stock:</b> {materiales.length} materiales</span>
            </span>
            {estadoAlistamiento === 'sin_alistar' && (
              <button
                type="button"
                onClick={() => handleTransicionAlistamiento('en_solicitud')}
                style={{
                  background: '#d97706',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '3px 8px',
                  fontSize: '10.5px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
                title="Alertar requerimiento urgente a Bodega y Alistamientos"
              >
                Alertar Bodega
              </button>
            )}
          </div>
        )}

        {/* Panel Directo de Alistamiento en Obra (2 Niveles: Insumos 100% y Fila Estado/Acción) */}
        <div className={estilos.panelAlistamientoDirecto}>
          {/* Nivel 1: Botón clickeable a ancho completo para gestionar insumos */}
          <button
            type="button"
            className={estilos.btnInsumosModalAnchoCompleto}
            onClick={() => setModalInsumosAbierto(true)}
            title="Gestionar materiales y herramientas requeridas para este módulo"
          >
            <div className={estilos.insumosInfoTexto}>
              <IconoHerramienta />
              <span>{herramientas.length} herr · {materiales.length} mat</span>
            </div>
            <span className={estilos.insumosTagAccion}>+ Gestionar</span>
          </button>

          {/* Nivel 2: Fila inferior agrupando estado y botón de acción */}
          <div className={estilos.filaEstadoYAccionAlistamiento}>
            <span className={`${estilos.chipAlistamiento} ${estilos['chipAlistamiento_' + estadoAlistamiento]}`}>
              {estadoAlistamiento === 'sin_alistar' && (
                <><IconoCaja /> Sin alistar</>
              )}
              {estadoAlistamiento === 'en_solicitud' && (
                <><IconoReloj /> Solicitado a bodega</>
              )}
              {estadoAlistamiento === 'aprobado' && (
                <><IconoCheck /> Aprobado bodega</>
              )}
              {estadoAlistamiento === 'alistado' && (
                <><IconoCaja /> Alistado bodega</>
              )}
              {estadoAlistamiento === 'despachado' && (
                <><IconoCheck /> Despachado</>
              )}
            </span>

            {estadoAlistamiento === 'sin_alistar' && (
              <button
                type="button"
                className={estilos.btnSolicitarAlistamiento}
                onClick={() => handleTransicionAlistamiento('en_solicitud')}
                title="Enviar requerimiento de materiales y herramientas a la bandeja 'Para alistar' de bodega"
              >
                <IconoCaja />
                <span>Solicitar</span>
              </button>
            )}

            {estadoAlistamiento === 'en_solicitud' && (
              <button
                type="button"
                className={estilos.btnRevertirAlistamiento}
                onClick={() => handleTransicionAlistamiento('sin_alistar')}
                title="Cancelar solicitud y volver a borrador"
              >
                <IconoCerrar />
                <span>Cancelar</span>
              </button>
            )}

            {estadoAlistamiento === 'alistado' && (
              <button
                type="button"
                className={estilos.btnDespacharCuadrilla}
                onClick={() => handleTransicionAlistamiento('despachado')}
                title="Registrar entrega y salida a la cuadrilla de obra"
              >
                <IconoCheck />
                <span>Despachar a obra</span>
              </button>
            )}

            {estadoAlistamiento === 'despachado' && (
              <button
                type="button"
                className={estilos.btnRevertirAlistamiento}
                onClick={() => handleTransicionAlistamiento('sin_alistar')}
                title="Reiniciar ciclo de alistamiento si se requieren insumos adicionales"
              >
                <span>↺ Reabrir</span>
              </button>
            )}
          </div>
        </div>

        {/* Barra de progreso y avance físico por cantidad ejecutada (Tarea 2.1) */}
        <div className={estilos.moduloProgresoContenedor}>
          <div className={estilos.infoFisicaFila}>
            <div className={estilos.cantidadesFisicasBox}>
              <span className={estilos.etiquetaCantidadFisica}>Ejecutado:</span>
              <strong className={estilos.valorCantidadFisica}>
                {Number(modulo.cantidad_ejecutada || 0).toLocaleString('es-CO')} / {Number(modulo.cantidad_contratada || modulo.cantidad || 0).toLocaleString('es-CO')} {modulo.unidad || 'UND'}
              </strong>
            </div>

            {/* Semáforo de atraso contra cronograma prometido */}
            <span
              className={`${estilos.semaforoBadge} ${
                semaforo.estado === 'al_dia'
                  ? estilos.semaforoAlDia
                  : semaforo.estado === 'alerta_atraso'
                  ? estilos.semaforoAlerta
                  : estilos.semaforoCritico
              }`}
              title={`Avance Real: ${Math.round(modulo.avance)}% | Esperado a hoy: ${semaforo.esperadoPct}%`}
            >
              <span className={estilos.semaforoDot} />
              {semaforo.estado === 'al_dia'
                ? 'Al día'
                : `Atraso ${semaforo.atrasoPct}%`}
            </span>

            {/* Botón Reportar Corte con Evidencia */}
            <button
              type="button"
              className={estilos.btnReportarCorteInline}
              onClick={() => setModalReportarAvanceAbierto(true)}
              title="Reportar cantidad ejecutada con foto de evidencia"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
              <span>Reportar Corte</span>
            </button>
          </div>

          <div className={estilos.moduloProgresoTrack}>
            <div
              className={`${estilos.moduloProgresoFill} ${
                modulo.avance === 100 ? estilos.progresoCompletado : ''
              }`}
              style={{ width: `${Math.min(100, Math.max(0, modulo.avance))}%` }}
            />
          </div>
          <span className={estilos.moduloProgresoPct}>{Math.round(modulo.avance)}%</span>
        </div>

        {/* Sección de Colaboradores asignados con estilos completos restaurados */}
        <footer className={estilos.moduloFooterColaboradores}>
          <div className={estilos.colaboradoresFila}>
            <div className={estilos.colaboradoresListaAvatares}>
              {tieneColaboradores ? (
                modulo.colaboradores!.map((c) => (
                  <span
                    key={c.id}
                    className={`${estilos.cuadrillaChipItem} ${
                      c.completado ? estilos.cuadrillaChipCompletado : ''
                    }`}
                    title={`${c.nombre} (${Math.round(c.avance_porcentaje || 0)}%)`}
                  >
                    <span className={estilos.avatarColaborador}>
                      {iniciales(c.nombre)}
                    </span>
                    <span className={estilos.cuadrillaNombreTexto}>{c.nombre}</span>
                    <span className={estilos.cuadrillaPctTexto}>
                      {Math.round(c.avance_porcentaje || 0)}%
                    </span>
                  </span>
                ))
              ) : (
                <span className={estilos.textoSinColaboradores}>Sin técnicos asignados</span>
              )}
            </div>

            {onAsignarColaboradores && (
              <button
                type="button"
                className={estilos.btnEditarColaboradores}
                onClick={() => setEditandoColaboradores(!editandoColaboradores)}
                title="Asignar colaboradores a este módulo"
              >
                <IconoColaborador />
                <span className={estilos.textoBtnColaborador}>
                  {tieneColaboradores ? 'Cuadrilla' : '+ Cuadrilla'}
                </span>
              </button>
            )}
          </div>

          {/* Subpanel desplegable para asignar colaboradores */}
          {editandoColaboradores && onAsignarColaboradores && (
            <SelectorColaboradoresModulo
              colaboradoresIds={modulo.colaboradores_ids || (modulo.colaboradores || []).map((c) => c.id)}
              onGuardar={(nuevosIds) => {
                onAsignarColaboradores(nuevosIds)
                setEditandoColaboradores(false)
              }}
              onCancelar={() => setEditandoColaboradores(false)}
            />
          )}
        </footer>
      </article>

      {/* Modal para reportar avance físico de corte con evidencia (Tarea 2.1) */}
      <ModalReportarAvance
        abierto={modalReportarAvanceAbierto}
        modulo={modulo}
        alCerrar={() => setModalReportarAvanceAbierto(false)}
        alGuardarCorte={handleGuardarCorteAvance}
      />

      {/* Modal para mover de etapa */}
      {modalMoverAbierto && onMoverAEtapa && (
        <ModalMoverAEtapa
          abierto={modalMoverAbierto}
          moduloCodigo={modulo.codigo}
          moduloNombre={modulo.nombre}
          etapaActualId={etapaActualId}
          etapas={todasLasEtapas}
          onConfirmarMover={(etapaDestinoId: string) => {
            onMoverAEtapa(etapaDestinoId)
            setModalMoverAbierto(false)
          }}
          onCerrar={() => setModalMoverAbierto(false)}
        />
      )}

      {/* Modal para añadir/gestionar insumos y herramientas */}
      {modalInsumosAbierto && (
        <ModalInsumosModulo
          abierto={modalInsumosAbierto}
          moduloNombre={modulo.nombre}
          moduloCodigo={modulo.codigo}
          elementosActuales={elementos}
          onGuardar={handleGuardarInsumos}
          onCerrar={() => setModalInsumosAbierto(false)}
        />
      )}

      {/* Modal Ajuste Rápido de AIU del APU Vinculado */}
      {apuVinculado && modalAiuApuAbierto && (
        <ModalEditarAiuApu
          abierto={modalAiuApuAbierto}
          apu={apuVinculado}
          onCerrar={() => setModalAiuApuAbierto(false)}
          onGuardar={(_id, nuevoAiu) => {
            actualizarAiuApu(apuVinculado.id, nuevoAiu)
            // Recalcular valor del módulo con nuevo AIU
            const admin = nuevoAiu.porcentaje_admin ?? apuVinculado.aiu.porcentaje_admin
            const imp = nuevoAiu.porcentaje_imprevistos ?? apuVinculado.aiu.porcentaje_imprevistos
            const util = nuevoAiu.porcentaje_utilidad ?? apuVinculado.aiu.porcentaje_utilidad
            const ivaU = nuevoAiu.porcentaje_iva_utilidad ?? apuVinculado.aiu.porcentaje_iva_utilidad
            const cd = apuVinculado.costo_directo
            const valAiu = cd * (admin / 100) + cd * (imp / 100) + cd * (util / 100) + (cd * (util / 100) * (ivaU / 100))
            const precioUnit = cd + valAiu
            const nuevoTotal = Math.round(precioUnit * (modulo.cantidad || 1))
            onActualizar({ valor_total: nuevoTotal })
          }}
        />
      )}
      {/* Modales de Confirmación y Advertencia Estilizados */}
      <ModalConfirmacion
        abierto={modalEliminarAbierto}
        titulo="Eliminar Tarea de Obra"
        mensaje={`¿Está seguro de eliminar el módulo "${modulo.nombre}"?`}
        submensaje="Se eliminarán sus registros de insumos y avance físico en esta etapa."
        textoConfirmar="Eliminar Módulo"
        tipo="peligro"
        alConfirmar={() => onEliminar && onEliminar()}
        alCancelar={() => setModalEliminarAbierto(false)}
      />

      <ModalConfirmacion
        abierto={modalSinInsumosAbierto}
        titulo="Insumos Requeridos"
        mensaje="Este módulo aún no tiene materiales ni herramientas cargadas."
        submensaje="Para solicitar despacho a bodega, primero debe añadir al menos un insumo al módulo."
        textoConfirmar="Gestionar Insumos"
        textoCancelar="Cerrar"
        tipo="advertencia"
        alConfirmar={() => setModalInsumosAbierto(true)}
        alCancelar={() => setModalSinInsumosAbierto(false)}
      />
    </>
  )
}
